import React, { type PropsWithChildren } from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import NFIcon from '@components/shared/NFIcon'
import { ScoreType } from '@models/Score'
import { getScoreType } from '@models/util/score.util'
import { formatPriceInCents } from '@utils/formatters/number.formatter'
import PageRoutes from '@utils/page-routes'

import type Score from '@models/Score'

interface Props {
  score: Score
  className?: string
}

const ScoreTypeLabel: React.FC<Props> = ({ score, className }: Props) => {
  const { t } = useTranslation()
  const scoreType = getScoreType(score)

  if (scoreType === ScoreType.PUBLISHED) {
    return (
      <Wrapper className={className}>
        <NFIcon icon="share" className="text-primary mr-1" />
        <span className="text-xs">{t('listing.published')}</span>
      </Wrapper>
    )
  }

  if (scoreType === ScoreType.SHARED) {
    return (
      <Wrapper className={className}>
        <NFIcon icon="share" className="text-primary mr-1" />
        <span className="text-xs">{t('listing.sharedScore')}</span>
      </Wrapper>
    )
  }

  if (scoreType === ScoreType.SELLING) {
    return (
      <Wrapper className={className}>
        <NFIcon icon="arrangeme" className="text-primary mr-1 text-lg" />
        <span className="text-xs">
          {formatPriceInCents(score.price ?? 0, {
            currency: score.currency,
            showDouble0ForCents: true,
          })}
        </span>
      </Wrapper>
    )
  }

  if (scoreType === ScoreType.TEMPLATE) {
    return (
      <Wrapper className={className}>
        <NFIcon icon="puzzle" className="text-primary mr-1" />
        {score.template_copies ? (
          <Link
            className="underline-link text-xs"
            onClick={(e) => {
              e.stopPropagation() // prevent viewing score
            }}
            to={PageRoutes.templateCopiesPath(score.external_id)}>
            {t('content.copyCount', { count: score.template_copies })}
          </Link>
        ) : (
          <span className="text-xs">{t('listing.template')}</span>
        )}
      </Wrapper>
    )
  }

  return (
    <Wrapper className={className}>
      <NFIcon icon="lock" className="text-gray-icons dark:text-gray-icons-dm mr-1" />
      <span className="text-xs">{t('listing.private')}</span>
    </Wrapper>
  )
}

export default ScoreTypeLabel

interface WrapperProps {
  className?: string
}
const Wrapper: React.FC<PropsWithChildren<WrapperProps>> = ({ className, children }) => (
  <p className={classNames('flex flex-row items-center', className)}>{children}</p>
)
